'use client';

import Image from 'next/image';
import Link from 'next/link';

import { useGTMPromotionBanner } from '@/fo/api/gtm';
import ArrowIcon from '@/fo/icons/base/icons/arrow24.svg';
import BiodermaLogo from '@/fo/icons/base/logos/bioderma.svg';
import EsthedermLogo from '@/fo/icons/base/logos/esthederm.svg';
import EtatPurLogo from '@/fo/icons/base/logos/etat-pur.svg';
import NaosLogo from '@/fo/icons/base/logos/naos.svg';
import { UiAdvertisingInformerBase } from '@/fo/ui/advertising/informer/base';

import { styles } from './UiKitSectionPrivilegeProgram.styles';

type NativeElement = React.HTMLAttributes<HTMLDivElement>;

type Link = { label: string; href: string; target?: boolean };

type Image = { alt: string; src: string };

export type Tag = {
  icon: string;
  alt: string;
  name: string;
};

export interface UiKitSectionPrivilegeProgramProps {
  className?: string;
  tags: Tag[];
  link: Link;
  title: string;
  backPicture: Image;
  titleMain: string;
  onClick?: NativeElement['onClick'];
  erid?: string;
  promotionName?: string;
  advertisingWarningText?: string;
}

export function UiKitSectionPrivilegeProgram({
  className,
  link,
  tags,
  title,
  titleMain,
  backPicture,
  erid,
  promotionName = '',
  advertisingWarningText,
  ...props
}: UiKitSectionPrivilegeProgramProps) {
  const slots = styles({});
  const { ref, trackSelectPromotion } = useGTMPromotionBanner({
    creativeName: backPicture.src,
    promotionId: link.href,
    promotionName:
      promotionName || backPicture.alt || titleMain || link.label || '',
    source: 'static',
    index: 1
  });

  return (
    <div
      ref={ref}
      className={slots.base({ className })}
      {...props}
    >
      <div className={slots.wrap()}>
        <Image
          className={slots.backPicture()}
          alt={backPicture.alt}
          width="800"
          height="753"
          src={backPicture.src}
        />
        <div className={slots.body()}>
          <div className={slots.top()}>
            <NaosLogo className={slots.logoMain()} />
            <div className={slots.logos()}>
              <BiodermaLogo className={slots.logo()} />
              <EsthedermLogo className={slots.logo()} />
              <EtatPurLogo className={slots.logo()} />
            </div>
          </div>
          <p className={slots.title()}>
            <span dangerouslySetInnerHTML={{ __html: title }}></span>{' '}
            <span className={slots.titleMain()}>{titleMain}</span>
          </p>
        </div>
        <div className={slots.bottom()}>
          <Link
            className={slots.link()}
            href={link.href}
            target={link.target ? '_blank' : '_self'}
            onClick={trackSelectPromotion}
          >
            {link.label}
            <span className={slots.linkArrow()}>
              <ArrowIcon className={slots.linkArrowIcon()} />
            </span>
          </Link>

          <div className={slots.tags()}>
            {tags.map((tag, index) => (
              <div
                key={index}
                className={slots.tag()}
              >
                <div className={slots.tagBack()}></div>
                <div className={slots.tagIconWrap()}>
                  <Image
                    className={slots.tagIcon()}
                    alt={tag.name}
                    width={32}
                    height={32}
                    src={tag.icon}
                  />
                </div>
                <div
                  className={slots.tagName()}
                  dangerouslySetInnerHTML={{ __html: tag.name }}
                />
              </div>
            ))}
          </div>
        </div>
      </div>
      {erid && (
        <UiAdvertisingInformerBase
          className={slots.advertisingInformer()}
          classNameContent={slots.advertisingInformerContent()}
          erid={erid || ''}
          advertisingWarningText={advertisingWarningText}
        />
      )}
    </div>
  );
}
